import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useDashboardStore } from '@/state/useDashboardStore'
import { useSettingsStore } from '@/state/useSettingsStore'
import { useTenant } from '@/tenant/TenantProvider'
import { useTrophyStore } from '@/state/useTrophyStore'
import { useViewStore } from '@/state/useViewStore'
import { monthKey } from '@/lib/workingDay'
import { now } from '@/lib/clock'
import { formatCzkShort, formatNumber, formatPercent } from '@/lib/format'
import { Sparkles, Trophy, X } from 'lucide-react'
import { playMotif, unlockAudio } from '@/lib/soundManager'
import { RevenueArea } from '@/components/charts/RevenueArea'
import { DEFAULT_MILESTONES } from '@/milestones/definitions'
import { describeMilestone } from '@/milestones/labels'
import { StoryProgressBars } from './StoryProgressBars'

const PAGE_DURATION_MS = 14_000
const TOTAL_PAGES = 6

export function MonthlyReportView() {
  const { t } = useTranslation()
  const snapshot = useDashboardStore((s) => s.snapshot)
  const locale = useSettingsStore((s) => s.locale)
  const setViewMode = useViewStore((s) => s.setViewMode)
  const markShown = useViewStore((s) => s.markMonthlyShown)
  const trophies = useTrophyStore((s) => s.unlocked)
  const tenant = useTenant()
  const [page, setPage] = useState(0)

  useEffect(() => {
    void unlockAudio()
    playMotif('monthly-intro')
  }, [])

  useEffect(() => {
    const id = window.setTimeout(() => {
      if (page < TOTAL_PAGES - 1) setPage((p) => p + 1)
      else {
        markShown(monthKey(now()))
        setViewMode('main')
      }
    }, PAGE_DURATION_MS)
    return () => window.clearTimeout(id)
  }, [page, setViewMode, markShown])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        markShown(monthKey(now()))
        setViewMode('main')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [setViewMode, markShown])

  if (!snapshot) {
    return (
      <div className="grid h-full w-full place-items-center text-ink-muted">
        <div className="text-center">
          <div className="mb-2 text-sm uppercase tracking-widest text-ink-subtle">
            {t('monthly.title')}
          </div>
          <div className="font-display text-2xl">{t('common.loading')}</div>
        </div>
      </div>
    )
  }

  const monthName = now().toLocaleDateString(locale === 'cs' ? 'cs-CZ' : 'en-GB', { month: 'long' })
  const trophyCount = trophies.filter(
    (tr) => Date.now() - new Date(tr.hitAt).getTime() < 30 * 86_400_000,
  ).length
  const recentTrophies = trophies.slice(-6).reverse()
  const defs = DEFAULT_MILESTONES.concat(tenant.milestones.customDefinitions ?? [])

  const pages: JSX.Element[] = [
    <Page key={0} kicker={t('monthly.title')} title={t('monthly.greeting', { name: monthName })}>
      <p className="max-w-2xl text-2xl leading-relaxed text-ink-muted">
        {locale === 'cs'
          ? `Měsíc skončil. Zhodnotíme, co se povedlo, kde nás to bolelo a co dál.`
          : `The month is in the books. Wins, lessons, and what's next.`}
      </p>
    </Page>,
    <Page
      key={1}
      kicker={t('monthly.summary')}
      title={locale === 'cs' ? `${monthName} v číslech` : `${monthName} in numbers`}
    >
      <div className="grid grid-cols-4 gap-6">
        <Stat label={t('kpi.mrr')} value={
            snapshot.revenue.mrrCzk == null
              ? '—'
              : formatCzkShort(snapshot.revenue.mrrCzk, locale)
          } />
        <Stat
          label={t('kpi.newClients')}
          value={formatNumber(snapshot.clients.newThisMonth, locale)}
        />
        <Stat label={t('kpi.churn')} value={formatPercent(snapshot.churn.ratePct, locale, 2)} />
        <Stat
          label={t('kpi.cashflow')}
          value={
            snapshot.revenue.cashflowMonthCzk == null
              ? '—'
              : formatCzkShort(snapshot.revenue.cashflowMonthCzk, locale)
          }
        />
      </div>
      <div className="mt-8 rounded-3xl border border-border bg-surface-1/70 p-4">
        <RevenueArea
          data={snapshot.history.mrr30d}
          targetCzk={snapshot.revenue.targetCzk}
          height={220}
        />
      </div>
    </Page>,
    <Page
      key={2}
      kicker={t('monthly.costs')}
      title={locale === 'cs' ? 'Náklady a marže' : 'Costs & margin'}
    >
      <div className="flex flex-col justify-center gap-6">
        <Stat
          label={locale === 'cs' ? 'Výdaje za měsíc' : 'Monthly expenses'}
          value={
            snapshot.costs.fixedMonthlyCzk == null
              ? '—'
              : formatCzkShort(snapshot.costs.fixedMonthlyCzk, locale)
          }
        />
        <Stat
          label={t('sales.margin')}
          value={
            snapshot.revenue.grossMarginPct == null
              ? '—'
              : formatPercent(snapshot.revenue.grossMarginPct, locale, 1)
          }
        />
        <Stat
          label={locale === 'cs' ? 'Na pobočku' : 'Per location'}
          value={
            snapshot.costs.fixedMonthlyCzk == null
              ? '—'
              : formatCzkShort(
                  snapshot.costs.fixedMonthlyCzk / Math.max(1, snapshot.clients.active),
                  locale,
                )
          }
        />
      </div>
    </Page>,
    <Page
      key={3}
      kicker={t('monthly.topLocations')}
      title={locale === 'cs' ? 'Hvězdy měsíce' : 'Stars of the month'}
    >
      <ol className="grid max-w-3xl grid-cols-2 gap-3">
        {snapshot.topLocations.slice(0, 6).map((loc, i) => (
          <li
            key={loc.id}
            className="flex items-center gap-3 rounded-2xl border border-border bg-surface-1/70 px-4 py-3 backdrop-blur"
          >
            <span className="grid size-9 place-items-center rounded-lg bg-brand-primary/15 font-mono text-base font-bold text-brand-primary">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-base font-semibold text-ink">{loc.name}</div>
              <div className="text-xs text-ink-subtle">{loc.city}</div>
            </div>
            <span className="font-mono text-base text-ink-muted">
              {formatCzkShort(loc.mrrCzk, locale)}
            </span>
          </li>
        ))}
      </ol>
    </Page>,
    <Page
      key={4}
      kicker={t('monthly.trophies')}
      title={
        trophyCount > 0
          ? locale === 'cs'
            ? `${trophyCount} milníků`
            : `${trophyCount} milestones`
          : locale === 'cs'
            ? 'Měsíc bez trofejí'
            : 'A month without trophies'
      }
    >
      {recentTrophies.length > 0 ? (
        <ul className="grid max-w-3xl grid-cols-2 gap-3">
          {recentTrophies.map((tr) => {
            const def = defs.find((d) => d.id === tr.milestoneId)
            const m = def && describeMilestone(def, locale)
            return (
              <li
                key={tr.milestoneId}
                className="flex items-center gap-3 rounded-2xl border border-brand-accent/30 bg-brand-accent/5 px-4 py-3"
              >
                <Trophy className="size-5 text-brand-accent" />
                <span className="text-lg text-ink">{m?.title ?? tr.milestoneId}</span>
              </li>
            )
          })}
        </ul>
      ) : (
        <p className="max-w-2xl text-2xl text-ink-muted">
          {locale === 'cs'
            ? 'Cíle byly před náš ambice. Pojďme si je vzít zpátky.'
            : 'Targets stayed ahead. Time to chase them down.'}
        </p>
      )}
    </Page>,
    <Page key={5} kicker={t('monthly.nextGoals')} title={t('monthly.closing')}>
      <div className="grid grid-cols-2 gap-4 text-lg text-ink-muted">
        <div className="rounded-2xl border border-border bg-surface-2/60 p-4">
          <div className="text-xs uppercase tracking-widest text-ink-subtle">
            {locale === 'cs' ? 'MRR cíl' : 'MRR goal'}
          </div>
          <div className="text-2xl text-ink">
            {formatCzkShort(snapshot.revenue.targetCzk, locale)}
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-surface-2/60 p-4">
          <div className="text-xs uppercase tracking-widest text-ink-subtle">
            {locale === 'cs' ? 'Nový měsíc, nové cíle' : 'New month, new goals'}
          </div>
          <div className="text-2xl text-ink">
            {locale === 'cs' ? 'Pojďme do toho.' : "Let's go."}
          </div>
        </div>
      </div>
    </Page>,
  ]

  function skip() {
    markShown(monthKey(now()))
    setViewMode('main')
  }

  return (
    <div className="relative grid h-full w-full place-items-center">
      <StoryProgressBars
        totalPages={TOTAL_PAGES}
        currentPage={page}
        durationMs={PAGE_DURATION_MS}
      />
      <button
        type="button"
        onClick={skip}
        title="Esc"
        className="absolute right-6 top-8 z-10 inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-1/60 px-3 py-1 text-xs font-medium text-ink-muted backdrop-blur transition hover:border-brand-accent/40 hover:text-ink"
      >
        <X className="size-3" />
        {locale === 'cs' ? 'Přeskočit' : 'Skip'}
      </button>
      <AnimatePresence mode="wait">
        <motion.div
          key={page}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-6xl px-8"
        >
          {pages[page]}
          <div className="mt-12 flex items-center justify-between text-xs text-ink-subtle">
            <span>
              {monthName} · {t('monthly.title')}
            </span>
            <span className="hidden items-center gap-2">
              {pages.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 rounded-full transition-all duration-300 ${i === page ? 'w-8 bg-brand-accent' : 'w-2 bg-border-strong/40'}`}
                />
              ))}
            </span>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

function Page({
  kicker,
  title,
  children,
}: {
  kicker: string
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-6">
      <div className="inline-flex items-center gap-2 rounded-full border border-brand-accent/40 bg-brand-accent/10 px-3 py-1 text-xs uppercase tracking-widest text-brand-accent">
        <Sparkles className="size-3.5" />
        {kicker}
      </div>
      <h1 className="font-display text-6xl font-bold leading-[1.05] tracking-tight text-ink">
        {title}
      </h1>
      {children}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <div className="font-display text-5xl font-bold tabular-nums text-ink">{value}</div>
      <div className="text-xs uppercase tracking-widest text-ink-subtle">{label}</div>
    </div>
  )
}
